<template>
  <div
    v-if="!lastSeenProductsLoading && lastSeenProducts.length"
    class="mx-5"
    data-test-id="recommendations_last_seen"
  >
    <div
      :class="{
        'my-10': !showReducedRecosSpacing,
        'mb-5 mt-10': showReducedRecosSpacing,
      }"
    >
      <span
        :class="{
          'text-xl font-normal': isPdp,
          'text-xl font-bold': !isPdp,
        }"
      >
        {{ $t('recommendations.title.last_seen') }}
      </span>
    </div>
    <BasicSlider
      :items="lastSeenProducts"
      key-prop="id"
      :pixel-gap="0"
      :center-content="false"
      data-test-id="recommendations_last_seen_slider"
      @slide-visible="onSlideVisible"
    >
      <template #item="{ item: product, index }">
        <ProductCard
          :id="product.id"
          :key="`product-recommendation-${product.id}`"
          :product="product"
          :siblings="getSiblingsColors(product) as ProductSibling[]"
          :colors="getProductAndSiblingsColors(product, 'colorRange')"
          :name="getName(product)"
          :link="toLocalePath(getProductDetailRoute(product))"
          :image="getProductImage(product.images, imageAssetType)"
          :disable-hover-image="true"
          :price="lowestPrice(product)"
          :show-add-to-cart="false"
          :show-add-to-wishlist="false"
          :show-available-colors="false"
          :hide-more-colors-label="true"
          :show-price-reduction="false"
          highlight-price-red
          class="px-1"
          @click:product="handleProductClick(product, index)"
        />
      </template>
    </BasicSlider>
  </div>
</template>

<script setup lang="ts">
import {
  getFirstAttributeValue,
  getProductAndSiblingsColors,
  type Product,
  type ProductSibling,
} from '@scayle/storefront-nuxt'
import ProductCard from '~/components/product/productCard/ProductCard.vue'
import BasicSlider from '~/components/ui/basicSlider/BasicSlider.vue'
import { useLastSeenProducts } from '../../composables/useLastSeenProducts'
import useCarouselListTracking from '../../composables/useCarouselListTracking'
import { LAST_SEEN_LIST_CONTEXT } from '../../constants/recommendations'
import useUiState from '~/composables/ui/useUiState'
import useExperiments from '~/composables/useExperiments'

const props = defineProps<{ productId?: number }>()

const emit = defineEmits(['click:recommendation'])

const { isPdp } = useUiState()
const { showReducedRecosSpacing } = useExperiments()

const { lastSeenProducts, isLoading: lastSeenProductsLoading } =
  useLastSeenProducts(props.productId)
const imageAssetType = '_f'

const { onSlideVisible, onSelect } = useCarouselListTracking({
  context: LAST_SEEN_LIST_CONTEXT,
  total: () => lastSeenProducts.value.length,
})

const handleProductClick = (product: Product, index: number) => {
  onSelect(product, index)
  emit('click:recommendation', product, index)
}

const { toLocalePath } = useRouteHelpers()

const getName = (product: Product) =>
  getFirstAttributeValue(product.attributes, 'name')?.label || ''

const lowestPrice = (product: Product) => getLowestPrice(product.variants || [])
</script>
